import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import DeviceModel from 'src/app/services/deviceModel.model';
import { DeviceModelService } from 'src/app/services/device-model.service';
import { ToastrService } from 'ngx-toastr';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import * as _ from 'lodash';

@Component({
  selector: 'app-device-models',
  templateUrl: './device-models.component.html',
  styleUrls: ['./device-models.component.scss']
})
export class DeviceModelsComponent implements OnInit, OnDestroy {
  public deviceModels: Array<DeviceModel> = [];
  public filteredDeviceModels: Array<DeviceModel> = [];
  public deviceModelsList: Array<DeviceModel> = [];
  public deviceModel: DeviceModel = new DeviceModel();
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public registerForm: FormGroup;
  public submitted = false;
  public deviceModelDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader: boolean = false;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  public showForm: boolean = false;
  public formFieldCount: number = 0;

  private searchTimeout: any;
  private scrollTimeout: any;
  private batchLoader: BatchLoader<any>;

  constructor(
    public formBuilder: FormBuilder,
    public deviceModelService: DeviceModelService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService,
    private toastr: ToastrService
  ) {
    this.deviceModel = new DeviceModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z0-9 _-]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  async ngOnInit() {
    this.search = '';
    this.deviceModels = [];
    this.filteredDeviceModels = [];
    this.deviceModelsList = [];
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    await this.init();
  }

  get f() {
    return this.registerForm.controls;
  }

  public async init() {
    try {
      this.enableDisablePageLoader = true;
      this.submitButton = false;
      this.updateButton = false;
      this.deviceModelDoubleClick = false;
      this.deviceModels = (await this.deviceModelService.getDeviceModals()) || [];

      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error: any) {
      this.toastr.error(error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredDeviceModels : this.deviceModels,
      (updatedList) => {
        this.deviceModelsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  public ngOnDestroy(): void {
    this.deviceModelService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public async applySearch(): Promise<void> {
    const value = this.search && this.search.trim().toLowerCase();
    this.isFiltering = !!value;

    const sourceList = this.isFiltering
      ? this.deviceModels.filter(model =>
          (model && model.name && model.name.toLowerCase().includes(value)) ||
          (model && model.abbreviation && model.abbreviation.toLowerCase().includes(value))
        )
      : this.deviceModels;

    this.filteredDeviceModels = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.deviceModelsList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.submitted = false;
    this.deviceModel = new DeviceModel();
    this.registerForm.reset();
  }

  public closeForm() {
    this.showForm = false;
    this.submitted = false;
    this.deviceModel = new DeviceModel();
    this.registerForm.reset();
  }

  public clear() {
    this.closeForm();
  }

  public async addUpdateDeviceModel() {
    this.submitted = true;
    if (this.registerForm.invalid) return;

    const formData = this.registerForm.value;
    if (this.submitButton) {
      const modelname = formData.name;
      const data = _.filter(this.deviceModels, (o) => o.name === modelname);
      if (data.length > 0) {
        this.toastr.warning('Device model name already available');
        this.deviceModelDoubleClick = false;
        return;
      }
    }

    try {
      this.deviceModelDoubleClick = true;
      if (this.submitButton) {
        await this.deviceModelService.create(formData);
        this.toastr.success('Device model created successfully');
      } else {
        const updateModel = { ...formData };
        delete updateModel.id;
        await this.deviceModelService.updateOneByID(this.deviceModel.id, updateModel);
        this.toastr.success('Device model updated successfully');
      }
      this.closeForm();
      await this.init();
    } catch (error: any) {
      this.toastr.error(error);
      this.deviceModelDoubleClick = false;
    }
  }

  public getDeviceModelById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.submitted = false;
    this.deviceModelDoubleClick = false;
    const found = this.deviceModels.find(m => m && m.id === id);
    if (found) {
      this.deviceModel = Object.assign({}, found);
      this.registerForm.patchValue({
        name: this.deviceModel.name,
        abbreviation: this.deviceModel.abbreviation,
        description: this.deviceModel.description
      });
    }
  }
}
